<template>
  <v-card
    id="enrollment_steps_card"
    :border="$role == 'specialist'"
    flat
    tile
  >
    <v-card-title class="tab-title d-flex">
      <v-btn
        v-if="showProviderProfileNavBtn"
        @click="emit('toggleProviderProfileNav')"
        :aria-label="$bct('Toggle provider profile navigation')"
        data-cy="provider_profile_nav_icon"
        icon="menu_open"
        variant="text"
      />
      <h2 class="v-card-title py-0 px-0 d-flex align-center">
        {{ $bct('How to enroll') }}
      </h2>
    </v-card-title>
    <v-divider />
    <v-card-text class="py-4">
      <p class="c-light-black fs-16 fw-500">
        {{ $bct(explanationText) }}
      </p>

      <div
        v-for="(step, stepIndex) in steps"
        :key="step.id"
      >
        <v-row class="mb-2">
          <LabeledTextfield
            v-model="step.title"
            :message="[$bct('Step'), stepIndex + 1].join(' ')"
            data-cy="step"
          />
          <LabeledControl message="Description">
            <RichTextEditor
              @change:text="changed = true"
              :ref="(el) => (enrollStepRefs[stepIndex] = el)"
              :initial-text="step.description"
              data-cy="step_description"
            />
          </LabeledControl>
        </v-row>

        <AttachmentUploader
          @uploaded="load()"
          ref="attachment_uploader"
          :owner="{ type: 'EnrollmentStep', id: step.id }"
          class="mb-4"
        />
        <AttachmentList
          v-if="(step.meta?.attachments?.length || 0) > 0"
          @delete="load"
          :attachments="step.meta.attachments"
          class="mb-4"
        />

        <div class="d-flex justify-space-between align-center mt-6">
          <StandardButton
            @click="destroy(step)"
            prepend-icon="close"
            role="destructive"
            text="Delete step"
          />
        </div>

        <v-divider class="my-8" />
      </div>

      <StandardButton
        v-if="!draftStepIsVisible"
        @click="addDraftStep()"
        role="create"
        size="x-large"
        text="Add step"
        full-width
      />

      <div v-if="draftStepIsVisible">
        <v-row class="mb-3">
          <LabeledTextfield
            v-model="newStep.title"
            message="New step"
          />
          <LabeledControl message="Description">
            <RichTextEditor
              @change:text="changed = true"
              ref="newEnrollmentStepRef"
              :initial-text="newStep.description"
            />
          </LabeledControl>
        </v-row>
        <div class="ta-right">
          <StandardButton
            @click="createStep()"
            :disabled-text="!newStep.title ? 'Title is required' : undefined"
            role="create"
            size="x-large"
            text="Create step"
            full-width
          />
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup>
import AttachmentList from '@/shared/components/attachments/AttachmentList.vue';
import AttachmentUploader from '@/shared/components/attachments/AttachmentUploader.vue';
import LabeledControl from '@/shared/components/form/LabeledControl.vue';
import LabeledTextfield from '@/shared/components/form/LabeledTextfield.vue';
import RichTextEditor from '@/shared/components/RichTextEditor.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';

const $bct = inject('translator');

const explanationText =
  'What are the steps that families need to take to enroll in your program? ' +
  'Add all relevant steps so they know what to expect. ' +
  'If you already have an electronic application or documents to complete, be sure to include them.';

const props = defineProps({
  providerId: {
    type: String,
    default: null,
  },
  requestProceedFromEnrollmentSteps: {
    type: Boolean,
    default: false,
  },
  showProviderProfileNavBtn: {
    type: Boolean,
    default: null,
  },
});

const emit = defineEmits(['error', 'moveForward', 'progress', 'toggleProviderProfileNav']);

const { api } = useApi();
const eventBus = useEventBus();

const newStepTemplate = {
  title: null,
  description: null,
};
const changed = ref(false);
const draftStepIsVisible = ref(false);
const enrollStepRefs = ref([]);
const isLoading = ref(false);
const newEnrollmentStepRef = ref(null);
const newStep = ref({ ...newStepTemplate });
const steps = ref([]);

watch(
  steps,
  () => {
    if (!isLoading.value) changed.value = true;
  },
  { deep: true },
);

watch(
  () => props.requestProceedFromEnrollmentSteps,
  async (newVal) => {
    if (newVal === true) {
      await updateAllRecords();
    }
  },
);

async function updateAllRecords() {
  if (!changed.value) {
    emit('moveForward');
    return;
  }

  await recordsMap()
    .then(() => {
      // None of the records contain an error, call an event emitter to move forward
      eventBus.chime('Changes saved');
      changed.value = false;
      emit('moveForward');
    })
    .catch(() => {
      // Call an error emitter to reset the requestProceedFromEnrollmentSteps watched value
      emit('error');
    });
}

async function recordsMap() {
  const results = await Promise.all(
    steps.value.map((step, index) => {
      const stepRef = enrollStepRefs.value[index];
      const html = getHTMLForRef(stepRef);
      return save(step, { html });
    }),
  );

  const errors = results.filter((response) => response?.error === true);
  if (errors.length > 0) throw errors;
}

function createStep() {
  getDraftHTML();
  api.manager.provider.enrollmentStep.create(
    props.providerId,
    newStep.value,
    () => {
      load();
    },
    (err) => {
      eventBus.error(err.response.data.errors[0]);
    },
  );
}

function destroy(step) {
  api.manager.provider.enrollmentStep.destroy(props.providerId, step.id, () => load());
}

function addDraftStep() {
  newStep.value = { ...newStepTemplate };
  draftStepIsVisible.value = true;
}

function load() {
  isLoading.value = true;
  api.manager.provider.enrollmentStep.index(props.providerId, async (resp) => {
    const loadedSteps = resp.data;

    steps.value = loadedSteps;
    calculateProgress(loadedSteps);

    if (loadedSteps.length === 0) {
      addDraftStep();
    } else {
      draftStepIsVisible.value = false;
    }

    await nextTick();
    changed.value = false;
    isLoading.value = false;
  });
}

function calculateProgress(steps) {
  const progress = steps.filter((step) => step.title).length > 0 ? 100 : 0;
  emit('progress', progress);
}

async function save(step, { html = null } = {}) {
  const updatedStep = { ...step, description: html };

  const response = await api.manager.provider.enrollmentStep.update(
    props.providerId,
    step.id,
    updatedStep,
  );

  if (response === undefined) {
    // The response has an error that's already been caught by handleError in provider
    return { error: true };
  }

  return response;
}

function getDraftHTML() {
  newStep.value.description = newEnrollmentStepRef.value.getSemanticHTML();
}

function getHTMLForRef(stepRef) {
  return stepRef.getSemanticHTML();
}

onMounted(load);
</script>
